// 80mm thermal receipt.
//
// Separate from the A4 invoice template on purpose. The A4 layout on a thermal
// roll is exactly the failure print-agent/README.md documents: a 210mm-wide
// page sent to an 80mm head prints garbled or blank. Receipts route to the
// branch's `receipt_printer_name`, so they need a document actually built for
// that paper.
//
// Self-contained by design: it takes the payment row and returns a complete
// HTML document. It does NOT scrape the on-screen preview's DOM, so the
// printed receipt cannot drift from what this file says just because someone
// restyles a preview.

// Roll width. Kept as a constant because it appears in three places that must
// agree — the @page size the agent parses, the body width, and the tests.
export const RECEIPT_WIDTH_MM = 80;

// Everything interpolated below is clinic-entered text (patient names, notes,
// reference numbers) landing in a document that gets written into a popup.
// Escape it here, once, rather than trusting each call site.
export function escapeHtml(value) {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

const money = (n) =>
  (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function formatDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (x) => String(x).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const PAYMENT_METHOD_TH = {
  cash: 'เงินสด',
  transfer: 'โอนเงิน',
  credit_card: 'บัตรเครดิต',
  debit_card: 'บัตรเดบิต',
  social_security: 'ประกันสังคม',
  insurance: 'ประกัน',
};

// A row of "label ....... value", which is the whole visual grammar of a
// thermal receipt. Values are right-aligned so the amounts form a column.
const row = (label, value) => `
  <div class="row"><span class="lbl">${escapeHtml(label)}</span><span class="val">${escapeHtml(value)}</span></div>`;

/**
 * @param payment  the row from /api/invoices/receipts/:payment_id
 * @param opts.print_lang    translator (key, fallback)
 * @param opts.autoPrint     true for the BROWSER fallback copy only. The agent
 *                           prints the document itself; a window.print() in the
 *                           copy it receives would fire a second print inside
 *                           its hidden window.
 * @param opts.amountInWords optional Thai amount-in-words line
 */
export function generateReceiptHTML(payment = {}, opts = {}) {
  const { print_lang = (k, f) => f, autoPrint = false, amountInWords = '' } = opts;
  const t = (key, fallback) => print_lang(key, fallback);

  const method = PAYMENT_METHOD_TH[payment.payment_method] || payment.payment_method || '';

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>${escapeHtml(payment.receipt_number || t('receipt', 'Receipt'))}</title>
<style>
  /* The agent reads this declaration's LENGTH straight out of the CSS text.
     \`80mm auto\` is invalid CSS (a length plus the auto keyword) so Chromium
     drops it from the CSSOM — but it is the only place the physical roll width
     is stated, and without it the page lays out at the printer's default paper
     and an 80mm roll prints blank. Do not "fix" it to a valid two-length size:
     the height genuinely is not known until the content is laid out. */
  @page { size: ${RECEIPT_WIDTH_MM}mm auto; margin: 0; }

  * { margin: 0; padding: 0; box-sizing: border-box; }
  body {
    width: ${RECEIPT_WIDTH_MM}mm;
    /* Sarabun is the face the rest of the printed documents use; the mono
       fallback keeps the amount column aligned if it is unavailable. */
    font-family: 'Sarabun', 'TH Sarabun New', monospace;
    font-size: 11px;
    line-height: 1.35;
    color: #000;
    background: #fff;
    padding: 4mm 3mm;
  }
  .center { text-align: center; }
  .shop { font-size: 13px; font-weight: 700; }
  .muted { font-size: 10px; }
  .title { font-size: 13px; font-weight: 700; margin: 3mm 0 2mm; }
  /* Dashes, not a border: a thermal head renders a repeated glyph more
     reliably than a hairline rule at this width. */
  .sep { border-top: 1px dashed #000; margin: 2mm 0; }
  .row { display: flex; justify-content: space-between; gap: 2mm; }
  .lbl { flex: 0 0 auto; }
  .val { flex: 1 1 auto; text-align: right; word-break: break-word; }
  .total { font-size: 15px; font-weight: 700; }
  .words { font-size: 10px; margin-top: 1mm; }
  .foot { margin-top: 4mm; font-size: 10px; }
  /* Rolls are cut short; give the cutter something to take. */
  .tail { height: 10mm; }
</style>
</head>
<body>
  <div class="center">
    <div class="shop">${escapeHtml(payment.branch_name || '')}</div>
    ${payment.branch_address ? `<div class="muted">${escapeHtml(payment.branch_address)}</div>` : ''}
    ${payment.branch_phone ? `<div class="muted">${t('phone', 'Tel')}: ${escapeHtml(payment.branch_phone)}</div>` : ''}
    ${payment.vat_number ? `<div class="muted">เลขประจำตัวผู้เสียภาษี: ${escapeHtml(payment.vat_number)}</div>` : ''}
    <div class="title">ใบเสร็จรับเงิน / RECEIPT</div>
  </div>

  <div class="sep"></div>
  ${row('เลขที่ / No.', payment.receipt_number || '')}
  ${row('วันที่ / Date', formatDate(payment.payment_date))}
  ${payment.invoice_number ? row('อ้างอิง / Inv.', payment.invoice_number) : ''}
  ${payment.patient_name ? row('ลูกค้า / Name', payment.patient_name) : ''}

  <div class="sep"></div>
  ${row('ชำระโดย / Method', method)}
  ${payment.payment_reference ? row('อ้างอิง / Ref.', payment.payment_reference) : ''}

  <div class="sep"></div>
  <div class="row total">
    <span class="lbl">รวม / TOTAL</span>
    <span class="val">${money(payment.amount)}</span>
  </div>
  ${amountInWords ? `<div class="words">(${escapeHtml(amountInWords)})</div>` : ''}

  <div class="sep"></div>
  <div class="center foot">
    ${t('thank_you', 'ขอบคุณที่ใช้บริการ / Thank you')}
  </div>
  <div class="tail"></div>
${autoPrint ? '<script>window.addEventListener("load",function(){setTimeout(function(){window.print();},350);});</script>' : ''}
</body>
</html>`;
}
